import groq from "groq";
import {
  buildFullPathProjection,
  staticFullPathProjection,
} from "./utils/build-full-path-projection";

// Office fields needed by the debug tool (from a pre-resolved `office` object)
const officeFieldsProjection = `
  officeID,
  locationName,
  urlSlug,
  enabled,
  address
`;

// Resolve office from a routeNode's context array
const officeFromContextProjection = `
  "office": *[_type == "office" && _id == ^.context[_type == "officeRef"][0]._ref][0]{
    ${officeFieldsProjection}
  }
`;

// Resolve home-care-service from a routeNode's context array
const serviceFromContextProjection = `
  "service": *[_type == "home-care-service" && _id == ^.context[_type == "homeCareServiceRef"][0]._ref][0]{
    _id,
    serviceName,
    description,
    displayOrder,
    isActive
  }
`;

// Resolve office content from a routeNode's context array (placeholder source of truth)
const officeContentFromContextProjection = `
  "officeContent": context[_type == "officeContentRef"][0]->{
    ...
  }
`;

const requiredPlaceholdersProjection = `
  "requiredPlaceholders": page->requiredPlaceholders
`;

const branchChildFieldsProjection = `
  "nodeSlug": slug.current,
  hideFromSitemap,
  "noIndex": page->seoSettings.noIndex,
  "lastModified": select(
    defined(page->_updatedAt) && page->_updatedAt >= _updatedAt => page->_updatedAt,
    true => _updatedAt
  ),
  ${serviceFromContextProjection},
  ${requiredPlaceholdersProjection}
`;

const branchChildTreeProjection = `
  "children": *[_type == "routeNode" && parent._ref == ^._id && isEnabled == true && defined(page._ref)] {
    ${branchChildFieldsProjection},
    "children": *[_type == "routeNode" && parent._ref == ^._id && isEnabled == true && defined(page._ref)] {
      ${branchChildFieldsProjection},
      "children": *[_type == "routeNode" && parent._ref == ^._id && isEnabled == true && defined(page._ref)] {
        ${branchChildFieldsProjection},
        "children": *[_type == "routeNode" && parent._ref == ^._id && isEnabled == true && defined(page._ref)] {
          ${branchChildFieldsProjection},
          "children": []
        }
      }
    }
  }
`;

const branchChildrenSubquery = `
  "children": *[
    _type == "routeNode" &&
    parent._ref == ^._id &&
    isEnabled == true &&
    defined(page._ref) &&
    (
      count(context[_type == "homeCareServiceRef"]) > 0 ||
      count(context[_type == "neighborhoodRef"]) > 0
    )
  ] {
    ${branchChildFieldsProjection},
    ${branchChildTreeProjection}
  }
`;

// Filter to only valid, enabled offices
const officeFilterProjection = `
  office != null &&
  office.enabled == true &&
  defined(office.urlSlug) &&
  defined(office.address.country)
`;

// Map resolved office to debug-friendly output fields
const officeOutputProjection = `
  "officeID": office.officeID,
  "officeName": office.locationName,
  "slug": office.urlSlug,
  "city": office.address.city,
  "state": office.address.stateProvince,
  "country": office.address.country
`;

// Build full URL path from route hierarchy (routeNode is source of truth).
const fullPathProjection = buildFullPathProjection();

/**
 * Query: ActiveServicePagesCombinedQuery (no params)
 * Single fetch that returns both hierarchical and branch-route service page data.
 * Use this in the debug tool to avoid two round-trips.
 * Country filtering and branch flattening are applied in the API/consumer.
 * Includes `noIndex` + `lastModified` to support sitemap filtering and lastmod.
 *
 * @note Tagged with `@sanity-typegen-ignore` because `buildFullPathProjection()`
 * is dynamic. Sanity TypeGen cannot evaluate the function body and would error
 * out. A static sibling, `typegenSafeActiveServicePagesQuery`, exists below
 * solely to give TypeGen a parseable shape; consumers continue to use this
 * runtime query.
 */
// @sanity-typegen-ignore
const ActiveServicePagesQuery = groq`{
  "hierarchical": *[
    _type == "routeNode" &&
    isEnabled == true &&
    defined(page._ref) &&
    count(context[_type == "homeCareServiceRef"]) > 0 &&
    defined(context[_type == "officeRef"][0]._ref)
  ] {
    _id,
    title,
    "nodeSlug": slug.current,
    ${fullPathProjection},
    ${officeFromContextProjection},
    ${officeContentFromContextProjection},
    ${serviceFromContextProjection},
    ${requiredPlaceholdersProjection}
  }[
    ${officeFilterProjection}
  ] | order(office.officeID asc, title asc) {
    ${officeOutputProjection},
    "serviceName": service.serviceName,
    "serviceSlug": nodeSlug,
    "type": "SB",
    hideFromSitemap,
    "noIndex": page->seoSettings.noIndex,
    "lastModified": select(
      defined(page->_updatedAt) && page->_updatedAt >= _updatedAt => page->_updatedAt,
      true => _updatedAt
    ),
    "pageUrl": fullPath,
    "office": office,
    "officeContent": officeContent,
    "homeCareService": service,
    "requiredPlaceholders": requiredPlaceholders
  },
  "branch": *[
    _type == "routeNode" &&
    isEnabled == true &&
    defined(linkedBranchRoots) &&
    count(context[_type == "officeRef"]) > 0 &&
    defined(context[_type == "officeRef"][0]._ref)
  ] {
    ${fullPathProjection},
    ${officeFromContextProjection},
    ${officeContentFromContextProjection},
    "branchRoots": linkedBranchRoots[]-> {
      "branchRootSlug": slug.current,
      isEnabled,
      hideFromSitemap,
      ${branchChildrenSubquery}
    }
  }[
    ${officeFilterProjection}
  ] {
    ...,
    "parentFullPath": fullPath,
    "office": office,
    "officeContent": officeContent
  }
}`;

/**
 * Static sibling of {@link ActiveServicePagesQuery} that exists ONLY for
 * Sanity TypeGen to extract a result type. The runtime query above uses
 * `buildFullPathProjection()`, which TypeGen cannot evaluate. Keep this
 * structurally in sync with the dynamic query above (only the `fullPath`
 * projection differs — static expansion vs. dynamic builder).
 *
 * Pattern mirrors `routeMatchQuery` in
 * `packages/site-builder/src/queries/route-data/build-route-data-query.ts`.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typegenSafeActiveServicePagesQuery = groq`{
  "hierarchical": *[
    _type == "routeNode" &&
    isEnabled == true &&
    defined(page._ref) &&
    count(context[_type == "homeCareServiceRef"]) > 0 &&
    defined(context[_type == "officeRef"][0]._ref)
  ] {
    _id,
    title,
    "nodeSlug": slug.current,
    ${staticFullPathProjection},
    ${officeFromContextProjection},
    ${officeContentFromContextProjection},
    ${serviceFromContextProjection},
    ${requiredPlaceholdersProjection}
  }[
    ${officeFilterProjection}
  ] | order(office.officeID asc, title asc) {
    ${officeOutputProjection},
    "serviceName": service.serviceName,
    "serviceSlug": nodeSlug,
    "type": "SB",
    hideFromSitemap,
    "noIndex": page->seoSettings.noIndex,
    "lastModified": select(
      defined(page->_updatedAt) && page->_updatedAt >= _updatedAt => page->_updatedAt,
      true => _updatedAt
    ),
    "pageUrl": fullPath,
    "office": office,
    "officeContent": officeContent,
    "homeCareService": service,
    "requiredPlaceholders": requiredPlaceholders
  },
  "branch": *[
    _type == "routeNode" &&
    isEnabled == true &&
    defined(linkedBranchRoots) &&
    count(context[_type == "officeRef"]) > 0 &&
    defined(context[_type == "officeRef"][0]._ref)
  ] {
    ${staticFullPathProjection},
    ${officeFromContextProjection},
    ${officeContentFromContextProjection},
    "branchRoots": linkedBranchRoots[]-> {
      "branchRootSlug": slug.current,
      isEnabled,
      hideFromSitemap,
      ${branchChildrenSubquery}
    }
  }[
    ${officeFilterProjection}
  ] {
    ...,
    "parentFullPath": fullPath,
    "office": office,
    "officeContent": officeContent
  }
}`;

export { ActiveServicePagesQuery };
